import { WEBSITE_ID_KEY } from "app/components/dashboard/widget-wizard/components/segments/constants";
import type {
    POPResponseEntity,
    SegmentsResponseEntity,
    SingleSegmentSeriesType,
} from "app/components/widget/widget-utilities/segments/types";
import type { ICustomSegmentsGroupAnalysisParams } from "app/services/segments/segmentsApiService";
import { granularities } from "pro-features/utils";

import markerWithDashedConfig from "pro-features/components/Chart/src/configs/series/markerWithDashedLinePerPointChartConfig";
import { SegmentsMonthlyVisitsGraphWidget } from "../SegmentsMonthlyVisitsGraphWidget";
import type { ChartType, DashboardMetric } from "../type";

export class SegmentsGroupMonthlyVisitsGraphWidget extends SegmentsMonthlyVisitsGraphWidget {
    protected metric: DashboardMetric;
    protected segmentIdsFormKeys;
    protected dataSource;

    constructor() {
        super();
        this.dataSource = "Visits";
        this.metric = "SegmentsGroupMonthlyVisits";
    }

    static getWidgetDashboardType() {
        return "Graph";
    }

    static getWidgetResourceType() {
        return "Graph";
    }

    static getWidgetMetadataType() {
        return "SegmentsGroupMonthlyVisitsGraph";
    }
    //Required to prepare keys in correct shape and omit errors
    public getLegendItems() {
        const properties = this._widgetConfig.properties;
        const selectedSegmentsId = properties.filters?.selectedSegmentsId;
        let keys = properties.key;

        if (typeof keys?.[0] !== "string" && selectedSegmentsId.length) {
            keys = selectedSegmentsId;
        } else {
            return;
        }

        const modifiedKeys = keys.map((item: string) => {
            const newItem = { icon: "", id: item, image: "", name: "" };

            if (newItem?.id?.startsWith(WEBSITE_ID_KEY)) {
                const name = item.replace(WEBSITE_ID_KEY, "");
                return { ...newItem, id: "", name };
            }

            return newItem;
        });

        return modifiedKeys;
    }

    public getSegmentsMetaData = () => {
        const state = this._reduxService.getState();

        const dashboardSegmentsMeta = state.segmentsModule?.dashboardSegmentsMeta;
        const customSegmentsMeta = state.segmentsModule.customSegmentsMeta;
        const segmentGroups = customSegmentsMeta?.SegmentGroups || dashboardSegmentsMeta?.SegmentGroups;

        const { selectedSegmentsId } = this._params;

        if (this.hasWebsite && segmentGroups) {
            segmentGroups.forEach((item) => {
                const websiteWithId = item.websites.map((website) => ({
                    ...website,
                    id: `${WEBSITE_ID_KEY}${website.domain}`,
                    isWebsite: true,
                }));

                this.currentSegmentsData.push(...websiteWithId);
            });
        }

        if (customSegmentsMeta) {
            // Build a map for quick lookups
            const accountSegmentsMap = customSegmentsMeta.AccountSegments.reduce((acc, segment) => {
                acc[segment.id] = segment;
                return acc;
            }, {});

            selectedSegmentsId?.forEach((id) => {
                const customSegment = accountSegmentsMap[id];
                if (customSegment) {
                    this.currentSegmentsData.push(customSegment);
                }
            });
            return;
        }

        if (dashboardSegmentsMeta) {
            this.currentSegmentsData.push(...dashboardSegmentsMeta.Segments);
        }
    };

    public fetchSegmentData = async (): Promise<POPResponseEntity | SegmentsResponseEntity> => {
        const { segmentsGroupId, country, webSource } = this._params;
        const timeGranularity = this.durationObject?.forAPI?.isWindow ? granularities[0] : granularities[2];
        const segmentGroupId = segmentsGroupId || this._widgetConfig.segmentsGroupId;

        if (segmentGroupId) {
            const apiParams = {
                compareFrom: this.durationObject?.forAPI?.compareFrom,
                compareTo: this.durationObject?.forAPI?.compareTo,
                segmentGroupId,
                country: country,
                from: this.durationObject.forAPI.from,
                to: this.durationObject.forAPI.to,
                webSource,
                isWindow: this.durationObject?.forAPI?.isWindow,
                includeSubDomains: true,
                timeGranularity,
                lastUpdated: this.currentSegmentsData[0].lastUpdated,
            } as ICustomSegmentsGroupAnalysisParams;

            return this.segmentApiService.getCustomSegmentsGroupAnalysis(apiParams);
        }
    };

    public async getChartSeries(data: ChartType): Promise<SingleSegmentSeriesType[]> {
        const { timeGranularity, selectedSegmentsId } = this._params;
        const result: SingleSegmentSeriesType[] = [];

        if (data) {
            const segmentsMeta = new Map();

            this.currentSegmentsData.forEach((item) => {
                segmentsMeta.set(item.id || item.domain, item);
            });

            selectedSegmentsId.forEach((segmentId: string) => {
                const dataById = data?.[segmentId]?.[this.dataSource]?.[timeGranularity]?.Graph;

                if (dataById) {
                    const isWebsite = segmentId.includes(WEBSITE_ID_KEY);
                    const formattedChartData = this.getFormattedChartData(dataById);

                    const currentSegmentMetaData = isWebsite
                        ? { domain: segmentId.replace(WEBSITE_ID_KEY, ""), isWebsite: true }
                        : segmentsMeta.get(segmentId);

                    const series = markerWithDashedConfig({
                        data: formattedChartData,
                        isDataSingleSeries: true,
                    }) as SingleSegmentSeriesType;

                    series.name = currentSegmentMetaData?.segmentName || currentSegmentMetaData?.domain;
                    series.displayName = currentSegmentMetaData?.domain;
                    series.isWebsite = currentSegmentMetaData?.isWebsite;

                    result.push(series);
                }
            });
        }

        return result;
    }

    public getProUrl() {
        const widgetProperties = this._widgetConfig?.properties;
        const { webSource, country, duration } = widgetProperties;

        const state = this._metricConfig.state;

        const result = {
            duration,
            country,
            webSource,
            id: widgetProperties.filters?.segmentsGroupId,
            mode: "group",
        };

        if (typeof state === "undefined") {
            return this._swNavigator.href("home", {}, {});
        }

        return this._swNavigator.href(state, result, {});
    }
}

SegmentsGroupMonthlyVisitsGraphWidget.register();
